import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ChangePasswordPage() {
    const { user, refreshUser, logout } = useAuth();
    const navigate = useNavigate();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (newPassword !== confirmPassword) {
            setError("New passwords do not match");
            return;
        }
        if (newPassword.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/change-password", {
                current_password: currentPassword,
                new_password: newPassword,
            });
            // Phase AUTH-S — change-password rotates the access token so all
            // prior sessions for this user (this device included) are
            // invalidated server-side. Swap the new token into storage
            // BEFORE refreshUser() so the next /auth/me call uses it.
            if (data?.token) {
                localStorage.setItem("rp_token", data.token);
            }
            toast.success("Password updated");
            const updated = await refreshUser();
            if (updated.role === "customer") navigate("/customer", { replace: true });
            else navigate("/admin", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSignOut = () => {
        logout();
        navigate("/login", { replace: true });
    };

    return (
        <AuthLayout
            eyebrow={user?.must_reset_password ? "Action required" : "Update password"}
            title={user?.must_reset_password ? "Set a new password to continue." : "Change your password."}
            subtitle={
                user?.must_reset_password
                    ? "For your security, please replace the temporary password assigned to your account."
                    : "Replace your current password with a new one. You'll stay signed in on this device."
            }
            footer={
                <button
                    type="button"
                    data-testid="change-password-signout"
                    onClick={handleSignOut}
                    className="text-sm font-medium text-slate-600 hover:text-slate-900 hover:underline"
                >
                    Sign out instead
                </button>
            }
        >
            <form onSubmit={onSubmit} className="space-y-5" data-testid="change-password-form">
                <div className="space-y-2">
                    <Label htmlFor="current_password">Current password</Label>
                    <Input
                        id="current_password"
                        data-testid="change-password-current-input"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="new_password">New password</Label>
                    <Input
                        id="new_password"
                        data-testid="change-password-new-input"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={8}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm new password</Label>
                    <Input
                        id="confirm_password"
                        data-testid="change-password-confirm-input"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={8}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                </div>

                {error && (
                    <div
                        data-testid="change-password-error"
                        className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100"
                    >
                        {error}
                    </div>
                )}

                <Button
                    type="submit"
                    data-testid="change-password-submit-button"
                    disabled={submitting}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white h-11"
                >
                    {submitting ? "Updating…" : "Update password"}
                </Button>
            </form>
        </AuthLayout>
    );
}
